/**
 * Hook useAgGridColumns para MerchandiseV3/List
 *
 * Define las columnas de AG Grid para la tabla de Entries.
 * Incluye cellRenderers personalizados.
 */
import { Button } from '@shared/components/ui/button';
import { CustomColDef } from '@shared/components/ui/AgGridTable';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLinkItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { Eye, MoreVertical, X } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Entry, ENTRY_STATUS_COLORS, EntryStatus, getEntryStatusLabel } from '../../models';

interface UseAgGridColumnsProps {
  selectedColumns?: string[];
  onCancelEntry?: (entry: Entry) => void;
  canCancel?: boolean;
  isTenant3pl?: boolean;
}

// Función utilitaria para formatear fechas
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return '-';
  }
};

export const useAgGridColumns = ({
  selectedColumns = [],
  onCancelEntry,
  canCancel = false,
  isTenant3pl = false,
}: UseAgGridColumnsProps) => {
  const { t } = useTranslation('merchandiseV3');

  const columnDefs: CustomColDef[] = useMemo(() => {
    const baseColumns: CustomColDef[] = [
      {
        field: 'reference',
        headerName: t('table.columns.reference', 'Referencia'),
        minWidth: 150,
        sortable: true,
        filter: 'agTextColumnFilter',
        cellRenderer: (params: any) => {
          const entry = params.data as Entry;
          return <span className="text-sm font-medium text-foreground">{entry.reference}</span>;
        },
      },
      {
        field: 'status',
        headerName: t('table.columns.status', 'Estado'),
        minWidth: 120,
        sortable: true,
        cellRenderer: (params: any) => {
          const entry = params.data as Entry;
          const status = entry.status as EntryStatus;
          const statusLabel = getEntryStatusLabel(status, t);
          const statusColor = ENTRY_STATUS_COLORS[status] || 'bg-gray-100 text-gray-800';

          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors ${statusColor}`}
            >
              {statusLabel}
            </span>
          );
        },
      },
      {
        field: 'estimated_delivery_at',
        headerName: t('table.columns.estimated_delivery_at', 'Fecha estimada'),
        minWidth: 150,
        sortable: true,
        cellRenderer: (params: any) => {
          const entry = params.data as Entry;
          return <span className="text-sm text-foreground">{formatDate(entry.estimated_delivery_at)}</span>;
        },
      },
      {
        field: 'boxes_count',
        headerName: t('table.columns.boxes_count', 'Cajas'),
        minWidth: 80,
        sortable: true,
        filter: 'agNumberColumnFilter',
        cellRenderer: (params: any) => {
          const entry = params.data as Entry;
          return <span className="text-sm text-foreground">{entry.boxes_count ?? '-'}</span>;
        },
      },
      {
        field: 'tracking_code',
        headerName: t('table.columns.tracking_code', 'Código seguimiento'),
        minWidth: 150,
        sortable: true,
        filter: 'agTextColumnFilter',
        cellRenderer: (params: any) => {
          const entry = params.data as Entry;
          return <span className="text-sm text-foreground">{entry.tracking_code || '-'}</span>;
        },
      },
      {
        field: 'created_at',
        headerName: t('table.columns.created_at', 'Fecha creación'),
        minWidth: 150,
        sortable: true,
        cellRenderer: (params: any) => {
          const entry = params.data as Entry;
          return <span className="text-sm text-foreground">{formatDate(entry.created_at)}</span>;
        },
      },
    ];

    // Añadir columna cliente solo para tenant_3pl
    if (isTenant3pl) {
      baseColumns.splice(2, 0, {
        field: 'client',
        headerName: t('table.columns.client', 'Cliente'),
        minWidth: 150,
        sortable: false,
        cellRenderer: (params: any) => {
          const entry = params.data as Entry;
          return <span className="text-sm text-foreground">{entry.team?.name || '-'}</span>;
        },
      });
    }

    // Añadir columna de acciones
    baseColumns.push({
      field: 'actions',
      headerName: '',
      sortable: false,
      filter: false,
      pinned: 'right',
      resizable: false,
      suppressSizeToFit: true,
      suppressAutoSize: true,
      maxWidth: 60,
      minWidth: 60,
      cellRenderer: (params: any) => {
        const entry = params.data as Entry;

        return (
          <div className="flex items-center justify-center h-full">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreVertical className="h-4 w-4" />
                  <span className="sr-only">{t('table.actions.menu', 'Abrir menú')}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLinkItem to={`/merchandise/edit/${entry.id}`}>
                  <Eye className="mr-2 h-4 w-4" />
                  {t('table.actions.view', 'Ver')}
                </DropdownMenuLinkItem>
                {entry.status === 'PENDIENTE' && canCancel && onCancelEntry && (
                  <DropdownMenuItem
                    onClick={() => onCancelEntry(entry)}
                    className="text-destructive focus:text-destructive"
                  >
                    <X className="mr-2 h-4 w-4" />
                    {t('table.actions.cancel', 'Cancelar')}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    });

    return baseColumns;
  }, [t, onCancelEntry, canCancel, isTenant3pl]);

  // Filtrar columnas según selectedColumns si está definido
  const filteredColumnDefs = useMemo(() => {
    if (selectedColumns.length === 0) {
      return columnDefs;
    }
    return columnDefs.filter((col) => selectedColumns.includes(col.field || ''));
  }, [columnDefs, selectedColumns]);

  return {
    columnDefs,
    filteredColumnDefs,
  };
};
